import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { generateText } from "ai";
import { createLovableAiGatewayProvider } from "@/lib/ai-gateway.server";
import type { Database } from "@/integrations/supabase/types";
import { CANONICAL_SERVICE_SLUGS } from "@/lib/service-categories";

/** Public, read-only services catalogue (safe for SSR on public routes). */
export const listServices = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`)
          h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
  const { data, error } = await supabasePublic
    .from("services")
    .select("id, slug, name, description")
    .eq("active", true)
    .in("slug", [...CANONICAL_SERVICE_SLUGS])
    .order("sort_order");
  if (error) throw new Error(error.message);
  const rows = data ?? [];
  const bySlug = new Map(rows.map((service) => [service.slug, service]));
  return CANONICAL_SERVICE_SLUGS
    .map((slug) => bySlug.get(slug))
    .filter((service): service is NonNullable<typeof service> => Boolean(service));
});

/** Public approved-partner directory. */
export const listPublicPartners = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`)
          h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
  const { data, error } = await supabasePublic
    .from("partner_profiles")
    .select("user_id, trading_name, headline, intro, services, service_areas, rating_avg, rating_count")
    .eq("published", true)
    .order("created_at", { ascending: false })
    .limit(60);
  if (error) throw new Error(error.message);
  return data ?? [];
});

/** Public partner profile: details, work portfolio and reviews (photos signed for 1h). */
export const getPartnerProfile = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ partnerId: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
    const supabasePublic = createClient<Database>(process.env["SUPABASE_URL"]!, key, {
      auth: { persistSession: false },
      global: {
        fetch: (input, init) => {
          const h = new Headers(init?.headers);
          if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`)
            h.delete("Authorization");
          h.set("apikey", key);
          return fetch(input, { ...init, headers: h });
        },
      },
    });
    const { data: profile } = await supabasePublic
      .from("partner_profiles")
      .select("user_id, trading_name, headline, intro, services, service_areas, rating_avg, rating_count, created_at")
      .eq("user_id", data.partnerId)
      .eq("published", true)
      .maybeSingle();
    if (!profile) return null;
    const [{ data: photos }, { data: reviews }] = await Promise.all([
      supabasePublic
        .from("partner_work_photos")
        .select("id, image_path, caption, created_at")
        .eq("partner_id", data.partnerId)
        .order("sort_order")
        .order("created_at", { ascending: false })
        .limit(60),
      supabasePublic
        .from("reviews")
        .select("id, rating, body, reviewer_name, photo_paths, created_at")
        .eq("partner_id", data.partnerId)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    // Narrow, safe projection of two public-facing profile fields; the profiles
    // table has no anon read policy, so this single lookup goes through admin.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: person }, { data: application }] = await Promise.all([
      supabaseAdmin
        .from("profiles")
        .select("username, avatar_path")
        .eq("id", data.partnerId)
        .maybeSingle(),
      supabaseAdmin
        .from("partner_applications")
        .select("company_number, years_experience, insurance_expires, status")
        .eq("user_id", data.partnerId)
        .eq("status", "approved")
        .maybeSingle(),
    ]);
    const serviceSlugs = profile.services ?? [];
    const { data: publicServices } = serviceSlugs.length
      ? await supabasePublic
          .from("services")
          .select("slug, name, description")
          .in("slug", serviceSlugs)
          .eq("active", true)
          .order("name")
      : { data: [] };
    const { signPhotoPaths } = await import("./photos.server");
    const avatarUrl = person?.avatar_path
      ? ((await signPhotoPaths("avatars", [person.avatar_path]))[0] ?? null)
      : null;
    const workUrls = await signPhotoPaths("work-photos", (photos ?? []).map((p) => p.image_path));
    const reviewPhotoPaths = (reviews ?? []).flatMap((r) => r.photo_paths ?? []);
    const reviewUrls = await signPhotoPaths("review-photos", reviewPhotoPaths);
    const urlByPath = new Map(reviewPhotoPaths.map((p, i) => [p, reviewUrls[i] ?? ""]));

    // Typical prices this partner has actually agreed on completed jobs.
    const { data: priceRows } = await supabasePublic.rpc("partner_price_stats", {
      _partner_id: data.partnerId,
    });
    const priceStats = (priceRows ?? []).map((r) => ({
      serviceId: r.service_id,
      serviceName: r.service_name,
      jobs: r.jobs,
      avgPricePence: r.avg_price_pence,
      minPricePence: r.min_price_pence,
      maxPricePence: r.max_price_pence,
    }));
    const totalJobs = priceStats.reduce((s, p) => s + p.jobs, 0);
    const overallAvgPence =
      totalJobs > 0
        ? Math.round(priceStats.reduce((s, p) => s + p.avgPricePence * p.jobs, 0) / totalJobs)
        : null;

    return {
      profile,
      priceStats,
      overallAvgPence,
      completedJobs: totalJobs,
      username: person?.username ?? null,
      avatarUrl,
      services: publicServices ?? [],
      businessDetails: application
        ? {
            companyNumber: application.company_number,
            yearsExperience: application.years_experience,
            insuranceExpires: application.insurance_expires,
          }
        : null,
      workPhotos: (photos ?? []).map((p, i) => ({ ...p, url: workUrls[i] ?? "" })),
      reviews: (reviews ?? []).map((r) => ({
        ...r,
        photos: (r.photo_paths ?? []).map((p) => urlByPath.get(p) ?? ""),
      })),
    };
  });


const serviceDiagnosisSchema = z.object({
  query: z.string().trim().min(4).max(500),
  services: z.array(z.object({
    id: z.string().uuid(),
    slug: z.string().min(1).max(100),
    name: z.string().min(1).max(160),
    description: z.string().nullable().optional(),
  })).min(1).max(100),
});

type ServiceDiagnosis = {
  summary: string;
  likelyCauses: string[];
  likelyFix: string;
  professional: string;
  urgency: "routine" | "soon" | "urgent" | "emergency";
  safetyNote: string | null;
  serviceSlugs: string[];
  confidence: "low" | "medium" | "high";
};

/** Advisory Shields AI triage for plain-English home problems. */
export const diagnoseServiceProblem = createServerFn({ method: "POST" })
  .inputValidator((value: unknown) => serviceDiagnosisSchema.parse(value))
  .handler(async ({ data }) => {
    const key = process.env["LOVABLE_API_KEY"];
    if (!key) return { ok: false as const, error: "Shields AI is temporarily unavailable" };

    const catalogue = data.services
      .map((service) => "- " + service.slug + ": " + service.name + (service.description ? " — " + service.description : ""))
      .join("\n");

    const { provider } = createLovableAiGatewayProvider(key);
    const result = await generateText({
      model: provider.responses("openai/gpt-5.6-sol"),
      system: [
        "You are the Shields Home Problem Expert inside a UK home-services booking app.",
        "Interpret a homeowner's plain-English symptom and help them understand the likely cause, what a professional would usually inspect or repair, who should handle it, and how urgent it may be.",
        "This is triage, not a definitive diagnosis. Never invent certainty from a short description.",
        "Never tell a customer to perform hazardous gas, electrical, structural, roofing-at-height, asbestos or other specialist work themselves.",
        "If there may be gas leakage, carbon monoxide, exposed live electrics, fire, major flooding, structural collapse or another immediate danger, classify it as emergency and give a short safety-first instruction.",
        "For gas/boiler work in the UK, refer to a Gas Safe registered engineer where relevant. For electrical work, refer to a qualified electrician where relevant.",
        "Do not recommend a service slug that is not in the supplied Shields catalogue. Prefer one best service, with at most two alternatives.",
        "Use concise polished British English.",
        "Output ONLY valid JSON, no markdown and no code fences.",
        'JSON shape: {"summary":"one-sentence explanation","likelyCauses":["up to 3 plausible causes"],"likelyFix":"what a professional would usually inspect/do","professional":"plain-English professional type","urgency":"routine|soon|urgent|emergency","safetyNote":"short safety note or null","serviceSlugs":["exact supplied slug"],"confidence":"low|medium|high"}',
      ].join("\n"),
      prompt: "Customer description:\n\"" + data.query + "\"\n\nAvailable Shields services:\n" + catalogue,
      providerOptions: {
        openai: { forceReasoning: true, reasoningEffort: "medium", store: false },
      },
    });

    let parsed: ServiceDiagnosis;
    try {
      parsed = JSON.parse(result.text) as ServiceDiagnosis;
    } catch {
      return { ok: false as const, error: "Shields AI could not interpret that description yet" };
    }

    const validSlugs = new Set(data.services.map((service) => service.slug));
    const serviceSlugs = (parsed.serviceSlugs ?? []).filter((slug) => validSlugs.has(slug)).slice(0, 3);
    if (!parsed.summary || !parsed.likelyFix || !parsed.professional || serviceSlugs.length === 0) {
      return { ok: false as const, error: "Shields AI needs a little more detail to point you in the right direction" };
    }

    const allowedUrgency = new Set(["routine", "soon", "urgent", "emergency"]);
    const allowedConfidence = new Set(["low", "medium", "high"]);
    return {
      ok: true as const,
      diagnosis: {
        summary: String(parsed.summary).slice(0, 320),
        likelyCauses: Array.isArray(parsed.likelyCauses)
          ? parsed.likelyCauses.map((cause) => String(cause).slice(0, 180)).slice(0, 3)
          : [],
        likelyFix: String(parsed.likelyFix).slice(0, 500),
        professional: String(parsed.professional).slice(0, 160),
        urgency: allowedUrgency.has(parsed.urgency) ? parsed.urgency : "routine",
        safetyNote: parsed.safetyNote ? String(parsed.safetyNote).slice(0, 420) : null,
        serviceSlugs,
        confidence: allowedConfidence.has(parsed.confidence) ? parsed.confidence : "low",
      },
    };
  });
